import { Boxes, ChevronRight, ShieldCheck } from 'lucide-react';
import type { ToolGroup } from '../../tools';

interface Props {
  groups: ToolGroup[];
  activeId: string;
  onSelect: (id: string) => void;
  isCollapsed: boolean;
  isMobileOpen: boolean;
}

export default function Sidebar({
  groups,
  activeId,
  onSelect,
  isCollapsed,
  isMobileOpen,
}: Props) {
  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex h-dvh w-72 shrink-0 flex-col border-r border-slate-200/90 bg-white/80 backdrop-blur-xl transition-[width,transform] duration-300 dark:border-white/[0.06] dark:bg-navy-900/70 md:sticky md:top-0 md:z-10 md:h-screen md:max-h-screen md:translate-x-0 ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full'
      } ${isCollapsed ? 'md:w-[68px]' : 'md:w-72'}`}
      aria-label={isCollapsed ? 'Collapsed tool navigation' : 'Tool navigation'}
    >
      <div
        className={`border-b border-slate-200/90 dark:border-white/[0.06] p-4 ${
          isCollapsed ? 'md:p-3' : ''
        }`}
      >
        <div
          className={`flex items-center gap-2.5 ${
            isCollapsed ? 'md:justify-center md:gap-0' : ''
          }`}
        >
          <div className="relative h-9 w-9 shrink-0 rounded-xl bg-gradient-to-br from-sky-400 via-cyan-400 to-indigo-500 grid place-items-center shadow-glow">
            <Boxes className="w-5 h-5 text-white" />
          </div>
          <div className={`leading-tight ${isCollapsed ? 'md:hidden' : ''}`}>
            <div className="flex items-center gap-1.5">
              <span className="font-display font-bold text-[15px] tracking-tight text-slate-900 dark:text-white">
                Cloud Integration Tools
              </span>
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              All-in-one · Offline · Private
            </div>
          </div>
        </div>
      </div>
      <nav
        className={`min-h-0 flex-1 overflow-y-auto p-3 space-y-5 ${
          isCollapsed ? 'md:p-2 md:space-y-3' : ''
        }`}
      >
        {groups.map((g) => (
          <div key={g.id}>
            <div
              className={`px-2 mb-1.5 text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-400 dark:text-slate-500 ${
                isCollapsed ? 'md:sr-only' : ''
              }`}
            >
              {g.label}
            </div>
            <div className={`space-y-0.5 ${isCollapsed ? 'md:space-y-1' : ''}`}>
              {g.tools.map((t) => {
                const Icon = t.icon;
                const isActive = t.id === activeId;
                return (
                  <button
                    key={t.id}
                    onClick={() => onSelect(t.id)}
                    title={isCollapsed ? t.name : undefined}
                    aria-label={isCollapsed ? t.name : undefined}
                    aria-current={isActive ? 'page' : undefined}
                    className={`nav-item w-full text-left ${
                      isCollapsed ? 'md:justify-center md:px-0' : ''
                    } ${isActive ? 'nav-item-active' : ''}`}
                  >
                    <Icon
                      className={`w-4 h-4 shrink-0 transition-colors ${
                        isActive ? 'text-sky-500 dark:text-sky-300' : ''
                      }`}
                    />
                    <span
                      className={`flex-1 truncate ${
                        isCollapsed ? 'md:sr-only' : ''
                      }`}
                    >
                      {t.name}
                    </span>
                    {t.badge && (
                      <span
                        className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-gradient-to-r from-sky-500/15 to-indigo-500/15 text-sky-600 dark:text-sky-300 border border-sky-500/20 ${
                          isCollapsed ? 'md:hidden' : ''
                        }`}
                      >
                        {t.badge}
                      </span>
                    )}
                    {isActive && (
                      <ChevronRight
                        className={`w-3.5 h-3.5 opacity-70 ${
                          isCollapsed ? 'md:hidden' : ''
                        }`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div
        className={`p-3 border-t border-slate-200/90 dark:border-white/[0.06] text-[11px] text-slate-500 dark:text-slate-400 ${
          isCollapsed ? 'md:hidden' : ''
        }`}
      >
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
          <span>100% client-side - no data leaves your machine</span>
        </div>
        <div className="mt-1.5 pl-5">
          Created by{' '}
          <span className="font-semibold bg-gradient-to-r from-sky-500 to-indigo-500 bg-clip-text text-transparent dark:from-sky-300 dark:to-indigo-300">
            Pavan Shetty
          </span>
        </div>
      </div>
    </aside>
  );
}
